import { createLogger } from '@svs/nova-core/log';

import { type Reduction, reductionValues } from '../../../schema/coupon/entry.js';
import type { DrawStateClient } from '../../../schema/draw/entry.js';
import type { Definition } from '../../../schema/frameworks/entry.js';
import { type CouponSettings, couponSettingsFormData, type SettingsKeys } from '../../../schema/sessionstore/entry.js';

const logger = createLogger('poolbetting.definition');
const DRAW_RESULT_STATES: Set<DrawStateClient> = new Set(['Live', 'Finished']);

type FeatureTypes = Definition['features'][number] | 'external' | 'RSYS' | 'USYS';

export interface IDefinition {
  get model(): Definition;
  get reductionSum(): Reduction;
  get betAmount(): Definition['defaultCouponValues']['betAmount'];
  get betAmounts(): Definition['betAmounts'];
  get systems(): Definition['systems'];
  get reduceModeCount(): Definition['defaultCouponValues']['reduceModeCount'];
  get outcomeGroupCount(): Definition['outcomes']['outcomeGroupCount'];
  get outcomeCount(): Definition['outcomes']['outcomeCount'];
  get outcomeHeatmap(): Definition['outcomes']['outcomeHeatmap'];
  get eventType(): Definition['outcomes']['eventType'];
  get drawsCount(): Definition['weeks'];
  get sportkryssFactors(): Definition['sportkryssFactors'];
  get maxBetAmount(): Definition['maxBetAmount'];
  get minBetAmount(): Definition['minBetAmount'];
  get loadDrawsApiPath(): Definition['loadDrawsApiPath'];
  get moduleEngine(): Definition['moduleEngine'];
  get drawProducts(): Definition['drawProducts'];
  get maxNumberOfCoupons(): Definition['maxNumberOfCoupons'];
  get tipsinfoOptions(): Definition['tipsinfoOptions'];
  get rSystems(): Definition['rSystems'];
  get uSystems(): Definition['uSystems'];
  get hasJackpot(): Definition['hasJackpot'];
  get defaultCouponSettingsValues(): CouponSettings;

  calculateCouponSettings({
    formData,
    currentCouponSettings,
    definition,
    settingsKey,
  }: {
    formData: FormData;
    currentCouponSettings: CouponSettings | undefined;
    definition: IDefinition;
    settingsKey: SettingsKeys;
  }): CouponSettings;

  hasFeature(...features: FeatureTypes[]): boolean;
  hasAdditions(): boolean;
  isProductIncluded(product: Definition['drawProducts'][number]): boolean;
}

const safeParseFormDataCouponSettings = (
  formData: FormData
): ReturnType<(typeof couponSettingsFormData)['parse']> | undefined => {
  try {
    const nextSettings = couponSettingsFormData.parse(formData);

    return nextSettings;
  } catch (error) {
    logger.error('Failed to parse coupon settings from form data', { error });
    return undefined;
  }
};

export class DefinitionEntity implements IDefinition {
  private readonly _model: Definition;
  private readonly availableFeatures: FeatureTypes[] = [];

  constructor(model: Definition) {
    this._model = model;
    this._initialize();
  }

  get model(): Definition {
    return this._model;
  }

  get reductionSum(): Reduction {
    const sum = this.model.reduction.reduce((sum, r) => r + sum, 0);

    try {
      return reductionValues.parse(sum);
    } catch {
      return 0;
    }
  }

  get betAmount(): Definition['defaultCouponValues']['betAmount'] {
    return this.model.defaultCouponValues.betAmount;
  }

  get betAmounts(): Definition['betAmounts'] {
    return this.model.betAmounts;
  }

  get systems(): Definition['systems'] {
    return this.model.systems;
  }

  get reduceModeCount(): Definition['defaultCouponValues']['reduceModeCount'] {
    return this.model.defaultCouponValues.reduceModeCount;
  }

  get outcomeGroupCount(): Definition['outcomes']['outcomeGroupCount'] {
    return this.model.outcomes.outcomeGroupCount;
  }

  get outcomeCount(): Definition['outcomes']['outcomeCount'] {
    return this.model.outcomes.outcomeCount;
  }

  get outcomeHeatmap(): Definition['outcomes']['outcomeHeatmap'] {
    return this.model.outcomes.outcomeHeatmap;
  }

  get eventType(): Definition['outcomes']['eventType'] {
    return this.model.outcomes.eventType;
  }

  get drawsCount(): Definition['weeks'] {
    return this.model.weeks;
  }

  get sportkryssFactors(): Definition['sportkryssFactors'] {
    return this.model.sportkryssFactors;
  }

  get maxBetAmount(): Definition['maxBetAmount'] {
    return this.model.maxBetAmount;
  }

  get minBetAmount(): Definition['minBetAmount'] {
    return this.model.minBetAmount;
  }

  get loadDrawsApiPath(): Definition['loadDrawsApiPath'] {
    return this.model.loadDrawsApiPath;
  }

  get moduleEngine(): Definition['moduleEngine'] {
    return this.model.moduleEngine;
  }

  get drawProducts(): Definition['drawProducts'] {
    return this.model.drawProducts;
  }

  get maxNumberOfCoupons(): Definition['maxNumberOfCoupons'] {
    return this.model.maxNumberOfCoupons;
  }

  get tipsinfoOptions(): Definition['tipsinfoOptions'] {
    return this.model.tipsinfoOptions;
  }

  get rSystems(): Definition['rSystems'] {
    return this.model.rSystems;
  }

  get uSystems(): Definition['uSystems'] {
    return this.model.uSystems;
  }

  get hasJackpot(): Definition['hasJackpot'] {
    return this.model.hasJackpot;
  }

  get defaultCouponSettingsValues(): IDefinition['defaultCouponSettingsValues'] {
    return {
      gameAnalysis: [],
      tipsinfo: ['Odds', 'SvenskaFolket'],
      outcomeLabel: 'Outcome',
      correction: [],
    } as const satisfies CouponSettings;
  }

  static hasDrawForecast(state: DrawStateClient): boolean {
    return DRAW_RESULT_STATES.has(state);
  }

  calculateCouponSettings({
    formData,
    currentCouponSettings,
    definition,
    settingsKey,
  }: {
    formData: FormData;
    currentCouponSettings: CouponSettings | undefined;
    definition: IDefinition;
    settingsKey: SettingsKeys;
  }): ReturnType<IDefinition['calculateCouponSettings']> {
    const formProps = safeParseFormDataCouponSettings(formData);

    if (!formProps) {
      const defaultSettings =
        settingsKey === 'PLAY'
          ? this.defaultCouponSettingsValues
          : { ...this.defaultCouponSettingsValues, tipsinfo: [] };
      return currentCouponSettings ?? defaultSettings;
    }

    const { tipsinfoFull, ...nextCouponSetting } = formProps;
    let { tipsinfo } = nextCouponSetting;
    const { gameAnalysis, outcomeLabel, correction } = nextCouponSetting;

    let allProductTipsinfo = definition.tipsinfoOptions.filter((option) => option !== 'Analysis');
    if (settingsKey !== 'PLAY') {
      allProductTipsinfo = allProductTipsinfo.filter(
        (option) => option === 'Odds' || option === 'SvenskaFolket' || option === 'FavoriteOdds'
      );
    }
    const isAllPreviousChecked = allProductTipsinfo.every((item) =>
      (currentCouponSettings?.tipsinfo ?? []).includes(item)
    );

    if (tipsinfoFull && !isAllPreviousChecked) {
      tipsinfo = allProductTipsinfo;
    }

    return {
      outcomeLabel,
      gameAnalysis,
      tipsinfo,
      correction,
    };
  }

  hasFeature(...features: FeatureTypes[]): ReturnType<IDefinition['hasFeature']> {
    return features.every((feature) => this.availableFeatures.includes(feature));
  }

  hasAdditions(): ReturnType<IDefinition['hasAdditions']> {
    /* TODO: This is only a first version to check if the product definition has additions. 
    Add more needed functionality here in later versions. */
    let hasAdditions = false;
    const { betAmounts } = this.model;
    if (betAmounts.length > 0) {
      hasAdditions = true;
    }
    return hasAdditions;
  }

  isProductIncluded(product: Definition['drawProducts'][number]): ReturnType<IDefinition['isProductIncluded']> {
    return this.model.drawProducts.includes(product);
  }

  private _initialize(): void {
    // Setup available features
    this.availableFeatures.push(...this.model.features.slice());

    if (this.model.systems.length > 1) {
      if (this.model.rSystems.length > 0) {
        this.availableFeatures.push('RSYS');
      }
      if (this.model.uSystems.length > 0) {
        this.availableFeatures.push('USYS');
      }
    }

    if ((this.model.maxExternalSystemSize ?? 0) > 0) {
      this.availableFeatures.push('external');
    }
  }
}
